import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { Agreement } from './types';

export function useAgreements() {
  const { clientId } = useCurrentClient();
  return useQuery({
    queryKey: ['agreements', clientId],
    enabled: !!clientId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Agreement[]> => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('agreements')
        .select('*')
        .eq('client_id', clientId)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Agreement[];
    },
  });
}

export interface AgreementInput {
  name: string;
  operator?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  notes?: string;
  isActive: boolean;
}

export function useUpsertAgreement() {
  const qc = useQueryClient();
  const { clientId } = useCurrentClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: AgreementInput }) => {
      if (!clientId) throw new Error('Cliente não selecionado');
      const payload = {
        client_id: clientId,
        name: input.name.trim(),
        operator: input.operator?.trim() || null,
        contact_name: input.contactName?.trim() || null,
        contact_phone: input.contactPhone?.trim() || null,
        contact_email: input.contactEmail?.trim() || null,
        notes: input.notes?.trim() || null,
        is_active: input.isActive,
      };
      if (id) {
        const { error } = await supabase.from('agreements').update(payload).eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('agreements').insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['agreements'] });
      toast.success(vars.id ? 'Convênio atualizado.' : 'Convênio cadastrado.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}

export function useDeleteAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('agreements')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agreements'] });
      qc.invalidateQueries({ queryKey: ['agreement_payments'] });
      toast.success('Convênio excluído.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}
